import { toError } from "@clipboard-health/util-ts";
import { useAppStateContext } from "@src/appV2/App/useAppState";
import { getCognitoIdTokenWithProactiveRefresh } from "@src/appV2/Auth/cognito/getCognitoIdTokenWithProactiveRefresh";
import { APP_V2_APP_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
import {
  type AppListenerHandle,
  addAppStateChangeListener,
} from "@src/appV2/NativeAdapters/app.adapter";
import { invalidateGetWorkerQuery } from "@src/appV2/Worker/api/useGetWorker";
import { getCognitoLocalStorageState } from "@src/lib/cognito/src/lib/cognitoLocalStorageInspector";
import { getCognitoSingleton } from "@src/lib/cognito/src/lib/cognitoSingleton";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

export const useAppStateListener = () => {
  const queryClient = useQueryClient();
  const { setIsAppActive } = useAppStateContext();

  useEffect(() => {
    let listenerHandle: AppListenerHandle | undefined;

    async function setupListener() {
      try {
        listenerHandle = await addAppStateChangeListener(async ({ isActive }) => {
          logEvent(APP_V2_APP_EVENTS.APP_STATE_CHANGE, {
            isActive,
          });
          setIsAppActive(isActive);
          if (!isActive) {
            return;
          }

          logEvent(APP_V2_APP_EVENTS.COGNITO_APP_RESUME_LOCALSTORAGE_STATE, {
            ...getCognitoLocalStorageState(),
          });

          // Force refresh Cognito token if available. Route through the
          // proactive-refresh wrapper so this shares the single-flight with
          // any axios interceptor calls firing concurrently on resume — a
          // raw fetchAuthSession({forceRefresh:true}) would race them and
          // trip RefreshTokenReuseException on Cognito.
          // Works around https://github.com/aws-amplify/amplify-js/issues/14618
          let cognitoUser;
          try {
            cognitoUser = await getCognitoSingleton().getCurrentUserInfo();
          } catch (maybeError: unknown) {
            logError(APP_V2_APP_EVENTS.FAILED_TO_REFRESH_COGNITO_TOKEN, {
              error: toError(maybeError),
            });
          }

          if (cognitoUser) {
            try {
              await getCognitoIdTokenWithProactiveRefresh(true);
            } catch {
              // Refresh failures are logged inside the wrapper.
            }
          }

          logEvent(APP_V2_APP_EVENTS.APP_STATE_CHANGE_INVALIDATE_GET_WORKER_QUERY);
          await invalidateGetWorkerQuery(queryClient);

          logEvent(APP_V2_APP_EVENTS.APP_RESTORED);
        });
      } catch (maybeError: unknown) {
        const error = toError(maybeError);

        logError(APP_V2_APP_EVENTS.FAILED_TO_SET_UP_APP_STATE_LISTENER, {
          error,
        });
      }
    }

    void setupListener();

    return () => {
      void listenerHandle?.remove();
    };
  }, [queryClient, setIsAppActive]);
};
